import { useState } from 'react'
import { localDayKey, type AbsenceDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { newUuid } from '../offline/uuid'
import { formatDateDE } from '../time/format'
import { useAbsenceTypes, useCancelAbsence, useCreateAbsence, useMyAbsences } from './useZeiten'

const STATUS_LABEL: Record<AbsenceDTO['status'], string> = {
  offen: 'offen',
  genehmigt: 'genehmigt',
  abgelehnt: 'abgelehnt',
  storniert: 'storniert',
}

export function absenceRangeLabel(a: AbsenceDTO): string {
  const from = formatDateDE(a.fromDay)
  return a.fromDay === a.toDay ? `${from}${a.halfDay ? ' (halber Tag)' : ''}` : `${from} – ${formatDateDE(a.toDay)}`
}

/**
 * „Meine Zeit": Abwesenheit beantragen (Typ/Zeitraum/halber Tag/Notiz) + eigene Anträge mit Status.
 * Ein offener Antrag lässt sich zurückziehen; entschieden wird im Chef-Tab „Abwesenheiten".
 */
export function AbsenceRequestCard() {
  // Server-Deckel: max. 2 Jahre voraus (Jahres-Tippfehler-Schutz) — das max-Attribut spiegelt ihn im Picker.
  const maxDay = localDayKey(Date.now() + 2 * 366 * 24 * 3600_000)
  const types = useAbsenceTypes()
  const mine = useMyAbsences()
  const create = useCreateAbsence()
  const cancel = useCancelAbsence()
  const [typeId, setTypeId] = useState('')
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const [halfDay, setHalfDay] = useState(false)
  const [note, setNote] = useState('')

  const activeTypes = (types.data?.types ?? []).filter((t) => t.active === 1)
  const singleDay = fromDay !== '' && (toDay === '' || toDay === fromDay)
  const canSubmit = typeId !== '' && fromDay !== '' && !create.isPending

  const submit = (): void => {
    if (!canSubmit) return
    create.mutate(
      {
        type_id: Number(typeId),
        from_day: fromDay,
        to_day: toDay || fromDay,
        half_day: singleDay && halfDay,
        note: note.trim() || undefined,
        client_uuid: newUuid(),
      },
      {
        onSuccess: () => {
          setTypeId('')
          setFromDay('')
          setToDay('')
          setHalfDay(false)
          setNote('')
        },
      },
    )
  }

  return (
    <section className="wf-card" aria-label="Abwesenheiten">
      <h3 className="wf-card__title">Abwesenheit beantragen</h3>
      <div className="wf-absform">
        <label className="wf-field">
          <span className="wf-label">Art</span>
          <select className="wf-input" value={typeId} onChange={(e) => setTypeId(e.target.value)} aria-label="Abwesenheits-Art">
            <option value="">Bitte wählen…</option>
            {activeTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">Von</span>
          <input className="wf-input" type="date" aria-label="Von" min="2020-01-01" max={maxDay} value={fromDay} onChange={(e) => setFromDay(e.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">Bis</span>
          <input className="wf-input" type="date" aria-label="Bis" min={fromDay || '2020-01-01'} max={maxDay} value={toDay} onChange={(e) => setToDay(e.target.value)} />
        </label>
        {singleDay ? (
          <label className="wf-field wf-field--check">
            <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} /> Nur ein halber Tag
          </label>
        ) : null}
        <label className="wf-field">
          <span className="wf-label">Notiz (optional)</span>
          <input className="wf-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} aria-label="Notiz" />
        </label>
        <button type="button" className="wf-btn" onClick={submit} disabled={!canSubmit}>
          Antrag stellen
        </button>
        {create.isError ? (
          <p className="wf-absform__err" role="alert">
            {create.error instanceof ApiError ? create.error.message : 'Antrag konnte nicht gestellt werden.'}
          </p>
        ) : null}
        {create.isSuccess ? (
          <p role="status">
            {create.data?.absence.status === 'genehmigt'
              ? 'Abwesenheit eingetragen und genehmigt.' /* Freigabe-Rolle: Direkteintrag, es entscheidet niemand mehr */
              : 'Antrag eingereicht — dein Chef entscheidet.'}
          </p>
        ) : null}
      </div>

      {(mine.data?.absences ?? []).length > 0 ? (
        <ul className="wf-abslist" aria-label="Meine Abwesenheiten">
          {(mine.data?.absences ?? []).map((a) => (
            <li key={a.id} className="wf-abslist__row">
              <span>
                <b>{a.label}</b> {absenceRangeLabel(a)}
              </span>
              <span className={`wf-tag wf-abs--${a.status}`}>{STATUS_LABEL[a.status]}</span>
              {a.status === 'offen' ? (
                <button
                  type="button"
                  className="wf-btn wf-btn--ghost wf-btn--sm"
                  onClick={() => cancel.mutate({ id: a.id, body: { version: a.version } })}
                  disabled={cancel.isPending}
                >
                  Zurückziehen
                </button>
              ) : null}
              {a.status === 'abgelehnt' && a.decisionReason ? <span className="wf-abslist__reason">„{a.decisionReason}"</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
